/**
 * Hierarchy-specific extractors — turn raw cypher rows from the Plant
 * Viewer hierarchy queries (sites, lines, processes, areas,
 * line-control coverage) into typed Plant* shapes. Moved here from
 * `ontology-api.ts` so the endpoint definitions can stay thin (just
 * `transformResponse: (raw) => ({ … pkg helpers …})`).
 *
 * Pure functions, no RTK / no Neo4j driver, so they're trivially
 * unit-testable.
 */

import { extractNode, toIntCount } from '../graph-row'
import { compareByNameThenId } from '../sort'
import { extractControllerRefs } from '../refs'
import type {
  PlantArea,
  PlantAreaSummary,
  PlantController,
  PlantLine,
  PlantLineControlEquipment,
  PlantLineControlNode,
  PlantLineSummary,
  PlantProcess,
  PlantProcessImplementation,
  PlantSite
} from '../types'

/**
 * Cypher `collect(distinct {area, subAreaCount, equipmentCount,
 * processCount, partCount, site, parent})` from `getPlantAreas` /
 * `getPlantAreaDetail`. Filter sentinel rows (area missing) and
 * project into typed `PlantAreaSummary`.
 */
export function extractAreaSummaries(value: unknown): PlantAreaSummary[] {
  if (!Array.isArray(value)) return []
  const out: PlantAreaSummary[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const area = extractNode<PlantArea>(obj['area'])
    if (!area) continue
    out.push({
      ...area,
      subAreaCount: toIntCount(obj['subAreaCount']),
      equipmentCount: toIntCount(obj['equipmentCount']),
      processCount: toIntCount(obj['processCount']),
      partCount: toIntCount(obj['partCount']),
      site: extractNode<PlantSite>(obj['site']),
      parent: extractNode<PlantArea>(obj['parent'])
    })
  }
  out.sort(compareByNameThenId)
  return out
}

/** Present array (possibly empty) means the query returned live member ids. */
function extractStringIds(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value.filter((id): id is string => typeof id === 'string' && id.length > 0)
}

/**
 * Cypher `collect(distinct {line, process, sublineCount, equipmentCount,
 * equipmentIds})` from `getPlantSiteDetail` / line-detail subline collection.
 * Filter sentinel rows (line missing) and project into `PlantLineSummary`.
 *
 * Also collapses duplicate summaries that share a line id. Site/line detail
 * Cypher already collapses `INSTANTIATES` fan-out, but a defensive merge
 * keeps Plant View from rendering one card per process if a stale or
 * hand-built payload still carries multiple maps for the same line.
 */
export function extractLineSummaries(value: unknown): PlantLineSummary[] {
  if (!Array.isArray(value)) return []
  const byId = new Map<string, PlantLineSummary>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const line = extractNode<PlantLine>(obj['line'])
    if (!line) continue
    const summary: PlantLineSummary = {
      ...line,
      process: extractNode<PlantProcess>(obj['process']),
      sublineCount: toIntCount(obj['sublineCount']),
      equipmentCount: toIntCount(obj['equipmentCount']),
      equipmentIds: extractStringIds(obj['equipmentIds'])
    }
    const existing = byId.get(line.id)
    if (!existing) {
      byId.set(line.id, summary)
      continue
    }
    byId.set(line.id, preferLineSummary(existing, summary))
  }
  const out = Array.from(byId.values())
  out.sort(compareByNameThenId)
  return out
}

/**
 * When two summary maps share a line id (multi-`INSTANTIATES` fan-out), keep
 * one card worth of data: prefer a non-null process (name/id ordered when
 * both are set) and the larger equipment/subline counts so KPIs don't
 * under-count if one of the duplicate rows was partially aggregated.
 */
function preferLineSummary(a: PlantLineSummary, b: PlantLineSummary): PlantLineSummary {
  const process = preferProcess(a.process, b.process)
  return {
    ...a,
    process,
    sublineCount: Math.max(a.sublineCount, b.sublineCount),
    equipmentCount: Math.max(a.equipmentCount, b.equipmentCount),
    equipmentIds: preferEquipmentIds(a.equipmentIds, b.equipmentIds)
  }
}

function preferProcess(a: PlantProcess | null, b: PlantProcess | null): PlantProcess | null {
  if (!a) return b
  if (!b) return a
  return compareByNameThenId(a, b) <= 0 ? a : b
}

function preferEquipmentIds(
  a: string[] | undefined,
  b: string[] | undefined
): string[] | undefined {
  if (a === undefined) return b
  if (b === undefined) return a
  if (b.length > a.length) return b
  return a
}

/**
 * Cypher `collect(distinct {line, site, sublineParent})` from
 * `getPlantProcessDetail`. Filter sentinel rows where no line was
 * matched (line will fail `extractNode`), and project each row into a
 * typed `PlantProcessImplementation`.
 */
export function extractProcessImplementations(value: unknown): PlantProcessImplementation[] {
  if (!Array.isArray(value)) return []
  const out: PlantProcessImplementation[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const line = extractNode<PlantLine>(obj['line'])
    if (!line) continue
    out.push({
      line,
      site: extractNode<PlantSite>(obj['site']),
      sublineParent: extractNode<PlantLine>(obj['sublineParent'])
    })
  }
  // Sort case-insensitively by `line.name` (the primary label shown on
  // the implementation card), falling back to the owning `site.name`
  // and finally `line.id` so two same-named lines on different sites
  // group by site and stay stable across renders.
  out.sort((a, b) => {
    const lineCmp = compareByNameThenId(a.line, b.line)
    if (lineCmp !== 0) return lineCmp
    const aSite = (a.site?.name ?? a.site?.id ?? '').toLowerCase()
    const bSite = (b.site?.name ?? b.site?.id ?? '').toLowerCase()
    if (aSite !== bSite) return aSite.localeCompare(bSite)
    return (a.line.id ?? '').localeCompare(b.line.id ?? '')
  })
  return out
}

/**
 * Project the equipment list inside one control-coverage line row
 * (`getPlantLineControlCoverage`). Each entry is a plain Cypher map
 * `{id, name, type, controllers: [Controller node, …]}`; controllers
 * are re-used via {@link extractControllerRefs} (which name-sorts and
 * drops sentinel nulls).
 */
function extractLineControlEquipment(value: unknown): PlantLineControlEquipment[] {
  if (!Array.isArray(value)) return []
  const out: PlantLineControlEquipment[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const id = typeof obj['id'] === 'string' ? obj['id'] : null
    if (!id) continue
    const name = typeof obj['name'] === 'string' ? obj['name'] : id
    const type = typeof obj['type'] === 'string' ? obj['type'] : undefined
    out.push({
      id,
      name,
      ...(type ? { type } : {}),
      controllers: extractControllerRefs(obj['controllers'])
    })
  }
  out.sort(compareByNameThenId)
  return out
}

/**
 * Project the `lines` roll-up returned by `getPlantLineControlCoverage`
 * into typed {@link PlantLineControlNode}s. Each row is a plain Cypher
 * map (not a node), carrying its own direct controllers plus
 * equipment (each with controllers). The flat list is stitched into a
 * tree client-side via `parentId`.
 */
export function extractLineControlCoverage(value: unknown): PlantLineControlNode[] {
  if (!Array.isArray(value)) return []
  const out: PlantLineControlNode[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const id = typeof obj['id'] === 'string' ? obj['id'] : null
    if (!id) continue
    const name = typeof obj['name'] === 'string' ? obj['name'] : id
    out.push({
      id,
      name,
      parentId: typeof obj['parentId'] === 'string' ? obj['parentId'] : null,
      isRoot: obj['isRoot'] === true,
      ...(typeof obj['lineCode'] === 'string' ? { lineCode: obj['lineCode'] } : {}),
      controllers: extractControllerRefs(obj['controllers']),
      equipment: extractLineControlEquipment(obj['equipment'])
    })
  }
  return out
}

/**
 * Plain controller pass-through (`PlantController` extractor) — used by
 * tests / future callers that want a typed `PlantController[]`. The
 * hierarchy endpoints themselves project controllers down to refs.
 */
export function extractControllers(value: unknown): PlantController[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => extractNode<PlantController>(item))
    .filter((c): c is PlantController => c !== null)
}
